import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { ICssConfigItem, IProcessDesign } from 'src/app/user-identification/models/user-identification';
import {
	getDesignAssetRefreshDelayMs,
	normalizeDesignAssetSrc,
	resolveBrandingAssetSrc,
} from './design/design-asset-src.util';
import {
	CSS_CONFIG_TEXT_COLOR_GRANULAR_KEYS,
	CSS_CONFIG_TEXT_COLOR_KEY,
	CSS_CONFIG_TEXT_COLOR_SEMANTIC_SYNC,
	CSS_CONFIG_TEXT_TYPOGRAPHY_KEY,
	isBrandCssConfigKey,
	resolveBrandOrphanVarNames,
	resolveCssConfigVarNames,
} from './design/css-config-variable-maps';
import { formatCssConfigValue } from './design/format-css-config-item';
import { syncGoogleFontsStylesheet } from './design/google-fonts.inject';

type DesignAssetLayer = 'primary' | 'fallback';

interface IDesignAssetPair {
	logo: string | null;
	custom_spinner: string | null;
	favicon: string | null;
}

const EMPTY_ASSETS: IDesignAssetPair = { logo: null, custom_spinner: null, favicon: null };
const DEFAULT_FAVICON_HREF = 'favicon.ico';

@Injectable({
	providedIn: 'root',
})
export class DesignService {
	private route = inject(ActivatedRoute);

	private darkMode: boolean = false;
	/** Nombres de propiedades CSS aplicadas en la última configuración (para limpiar al cambiar de diseño). */
	private appliedCssPropertyNames: string[] = [];
	private themeColorMeta: HTMLMetaElement | null = null;
	private faviconLink: HTMLLinkElement | null = null;

	private readonly assetsPrimary = signal<IDesignAssetPair>({ ...EMPTY_ASSETS });
	private readonly assetsFallback = signal<IDesignAssetPair>({ ...EMPTY_ASSETS });

	private readonly configDesignSignal = signal<IProcessDesign | null>(null);
	public readonly configDesign$: Observable<IProcessDesign | null> = toObservable(this.configDesignSignal);

	constructor() {
		this.route.queryParams.subscribe((params: Record<string, string>) => {
			if (params['skin'] && params['skin'] === 'dark') {
				this.darkMode = true;
			}
		});
	}

	public getConfigDesign(): Observable<IProcessDesign | null> {
		return this.configDesign$;
	}

	/**
	 * @param assetLayer `primary` = config_design (default); `fallback` = process.design en config (solo assets).
	 */
	public setConfigDesign(value: IProcessDesign | null, assetLayer: DesignAssetLayer = 'primary'): void {
		if (!value) {
			this.assetsPrimary.set({ ...EMPTY_ASSETS });
			this.assetsFallback.set({ ...EMPTY_ASSETS });
			this.applyVisualDesign(null);
			return;
		}
		const pair = this.toAssetPair(value);
		if (assetLayer === 'primary') {
			this.assetsPrimary.set(pair);
		} else {
			this.assetsFallback.set(pair);
		}
		const base =
			assetLayer === 'fallback'
				? { ...(this.configDesignSignal() ?? {}), ...value }
				: value;
		this.applyVisualDesign(base);
	}

	public patchShowWatermark(show: boolean): void {
		const current = this.configDesignSignal();
		if (current) {
			this.configDesignSignal.set({ ...current, showWatermark: show });
		}
	}

	public isDarkMode(): boolean {
		return this.configDesignSignal()?.skin === 'dark' || this.darkMode;
	}

	/**
	 * Spinner SVG definido en admin (diseño); se usa si no hay `img` explícito en el loader.
	 */
	public getCustomSpinnerUrl(): string | null {
		const p = this.assetsPrimary();
		const f = this.assetsFallback();
		return resolveBrandingAssetSrc(p.custom_spinner, f.custom_spinner);
	}

	/** Delay hasta refresh de config_design; null si no hay URL firmada en primario. */
	public getDesignAssetRefreshDelayMs(nowMs: number = Date.now()): number | null {
		const p = this.assetsPrimary();
		return getDesignAssetRefreshDelayMs(p.logo, p.custom_spinner, nowMs, p.favicon);
	}

	private toAssetPair(design: IProcessDesign): IDesignAssetPair {
		return {
			logo: normalizeDesignAssetSrc(design.logo ?? null),
			custom_spinner: normalizeDesignAssetSrc(design.custom_spinner ?? null),
			favicon: normalizeDesignAssetSrc(design.favicon ?? null),
		};
	}

	private withEffectiveAssets(base: IProcessDesign): IProcessDesign {
		const p = this.assetsPrimary();
		const f = this.assetsFallback();
		const logo = resolveBrandingAssetSrc(p.logo, f.logo);
		const customSpinner = resolveBrandingAssetSrc(p.custom_spinner, f.custom_spinner);
		const favicon = resolveBrandingAssetSrc(p.favicon, f.favicon);
		const published: IProcessDesign = { ...base, custom_spinner: customSpinner };
		if (logo) {
			published.logo = logo;
		} else {
			delete published.logo;
		}
		if (favicon) {
			published.favicon = favicon;
		} else {
			delete published.favicon;
		}
		return published;
	}

	private applyVisualDesign(design: IProcessDesign | null): void {
		this.clearAppliedDesignCssVars();
		if (!design) {
			this.setFavicon(null);
			this.setThemeColor(null);
			this.configDesignSignal.set(null);
			return;
		}
		const published = this.withEffectiveAssets(design);
		// El body debe tener la clase final antes de las vars (Infinity redefine tokens en .dark)
		this.toggleDarkMode(published.skin === 'dark' || this.darkMode);
		this.setCssConfig(
			published.css_config,
			published.skin === 'dark' || this.darkMode ? 'dark' : 'light',
		);
		this.setThemeColor(published.theme_color);
		this.setFavicon(published.favicon);
		this.configDesignSignal.set(published);
	}

	private clearAppliedDesignCssVars(): void {
		for (const name of this.appliedCssPropertyNames) {
			document.body.style.removeProperty(name);
			document.documentElement.style.removeProperty(name);
		}
		this.appliedCssPropertyNames = [];
		this.setCssConfigAppliedMarker(false);
	}

	private registerCssProperty(name: string, cssValue: string): void {
		document.body.style.setProperty(name, cssValue);
		this.appliedCssPropertyNames.push(name);
		this.setCssConfigAppliedMarker(true);
	}

	/** Marca body para anti-`.dark` en pwcs-process-theme (solo con vars aplicadas). */
	private setCssConfigAppliedMarker(applied: boolean): void {
		try {
			if (typeof document === 'undefined') {
				return;
			}
			if (applied) {
				document.body.setAttribute('data-pwcs-css-config', '');
			} else {
				document.body.removeAttribute('data-pwcs-css-config');
			}
		} catch {
			// DOM opcional en tests
		}
	}

	private setCssConfig(
		cssConfig: IProcessDesign['css_config'],
		skin: 'light' | 'dark' = 'light',
	): void {
		try {
			if (!cssConfig) {
				return;
			}
			if (Array.isArray(cssConfig)) {
				this.applyCssConfigArray(cssConfig, skin);
				return;
			}
			const legacy = cssConfig as unknown as { button_color?: string };
			if (legacy?.button_color) {
				this.registerCssProperty('--action-primary', legacy.button_color);
			}
		} catch {
			// Errores de DOM no deben propagarse
		}
	}

	private applyCssConfigArray(items: ICssConfigItem[], skin: 'light' | 'dark' = 'light'): void {
		const hasGranularTextColors = items.some((item) =>
			CSS_CONFIG_TEXT_COLOR_GRANULAR_KEYS.includes(item?.variable?.trim() ?? ''),
		);

		const brandItems: ICssConfigItem[] = [];
		const otherItems: ICssConfigItem[] = [];
		for (const item of items) {
			if (!item?.variable) {
				continue;
			}
			if (isBrandCssConfigKey(item.variable)) {
				brandItems.push(item);
			} else {
				otherItems.push(item);
			}
		}

		// brand_* primero (huérfanas); pickers después pueden divergir.
		for (const item of [...brandItems, ...otherItems]) {
			const variableKey = item.variable.trim();
			const cssValue = formatCssConfigValue(item);
			if (cssValue === null) {
				continue;
			}
			if (variableKey === CSS_CONFIG_TEXT_TYPOGRAPHY_KEY) {
				this.applyTypographyFromConfig(cssValue);
			}
			if (variableKey === CSS_CONFIG_TEXT_COLOR_KEY) {
				// Variable puente explícita para overrides quirúrgicos en pwcs-process-theme.
				this.registerCssProperty('--pwcs-text-color', cssValue);
				if (!hasGranularTextColors) {
					// Legacy: text_color pinta primary+secondary. Con text_color_* no pisar title/strong.
					for (const semanticVar of CSS_CONFIG_TEXT_COLOR_SEMANTIC_SYNC) {
						this.registerCssProperty(semanticVar, cssValue);
					}
				} else {
					// Solo puente PWCS; los tokens --text-* los aplican text_color_*.
					continue;
				}
			}
			if (isBrandCssConfigKey(variableKey)) {
				for (const varName of resolveBrandOrphanVarNames(variableKey, skin)) {
					this.registerCssProperty(varName, cssValue);
				}
				continue;
			}
			for (const varName of resolveCssConfigVarNames(item.variable)) {
				this.registerCssProperty(varName, cssValue);
			}
		}
	}

	/**
	 * Carga hojas web y aplica --pwcs-font-family en el orden correcto.
	 */
	private applyTypographyFromConfig(fontStack: string): void {
		try {
			if (typeof document === 'undefined') {
				return;
			}
			syncGoogleFontsStylesheet(document, fontStack);
		} catch {
			// DOM opcional en tests
		}
	}

	private setThemeColor(themeColor: string | null | undefined): void {
		try {
			if (themeColor === null || themeColor === undefined || themeColor === '') {
				this.themeColorMeta?.remove();
				document.querySelector('meta[name="theme-color"]')?.remove();
				this.themeColorMeta = null;
				return;
			}
			let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
			if (!meta) {
				meta = document.createElement('meta');
				meta.name = 'theme-color';
				document.head.appendChild(meta);
			}
			this.themeColorMeta = meta;
			meta.setAttribute('content', themeColor);
		} catch {
			// Meta opcional; no bloquear
		}
	}

	/** Aplica favicon del tema o restaura el estático por defecto. */
	private setFavicon(href: string | null | undefined): void {
		try {
			if (typeof document === 'undefined') {
				return;
			}
			const nextHref = href && String(href).trim() ? String(href).trim() : DEFAULT_FAVICON_HREF;
			let link =
				this.faviconLink ??
				document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
				document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]');
			if (!link) {
				link = document.createElement('link');
				link.rel = 'icon';
				document.head.appendChild(link);
			}
			this.faviconLink = link;
			if (nextHref.startsWith('data:image/svg+xml')) {
				link.type = 'image/svg+xml';
			} else if (!nextHref.startsWith('data:')) {
				link.type = nextHref.endsWith('.svg')
					? 'image/svg+xml'
					: nextHref.endsWith('.png')
						? 'image/png'
						: 'image/x-icon';
			}
			link.href = nextHref;
		} catch {
			// Favicon opcional; no bloquear
		}
	}

	private toggleDarkMode(isDark: boolean): void {
		if (isDark) {
			document.body.classList.add('dark');
		} else {
			document.body.classList.remove('dark');
		}
	}
}
